import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import reassignIcon from '../../../assets/icons/reassignicon.png';
import { CancelTicket, ReassignTicket } from '../../../components/Ticket';
import {
  DisplayModal,
  ModalMultiWrapper,
  SpinnerLoading,
} from '../../../components/Common';
import { useTicketDetails } from '../context/TicketDetailsProvider';
import { getAuthUser } from '../../../utils/auth.util';
import TicketDetailView from '../../../components/Ticket/TicketDetailView/TicketDetailView';
import CommentSection from '../../../components/Ticket/CommentSection/CommentSection';
import { ROLE_NAMES } from '../../../common/constant';
import { DateUtil } from '../../../utils';
import { CLOSED_TICKET_STATUS } from './constants';

import './HRTicketDetails.scss';

function HRTicketDetails() {
  document.title = 'Ticket Details | Envision Daikin';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const fr = queryParams.get('fr');
  const userId = getAuthUser()?.id;
  const {
    ticketDetails,
    roleType,
    ticketResponseListingLoading,
    displayModalOpen,
    displayModalFile,
    setDisplayModalOpen,
    TICKET_ROLE_TYPES,
    updateTicketMutation,
    cancelMyTicketMutation,
    error: ticketDetailsError,
    ticketStatusData,
    isLoading: isLoadingData,
  } = useTicketDetails();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: ticketId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modelName, setModelName] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);

  const roleName = getAuthUser()?.role?.Name;

  if (ticketDetailsError) {
    toast.error('Failed to fetch ticket details. Please try again.', {
      className: 'toast-error',
    });
    navigate(fr === 'HR' ? '/hr/tickets' : '/tickets');
  }

  const navigateBack = fromTab => {
    if (!fromTab) {
      navigate('/hr/tickets');
      return;
    }
    navigate(`/hr/tickets?t=${fromTab}`);
  };

  useEffect(() => {
    if (!ticketDetails) {
      return;
    }

    // If role is admin, no need to check permissions
    if (roleName === ROLE_NAMES.ADMIN) {
      return;
    }

    // HR Role Check
    if (roleName === ROLE_NAMES.HR) {
      if (ticketDetails?.AssignedToId !== userId) {
        navigateBack(fr);
      }
    }
  }, [ticketDetails]);

  const handleChangeStatus = async statusObj => {
    setSelectedStatus(statusObj);
  };

  const handleTicketStatusSave = async () => {
    await updateTicketMutation.mutateAsync({
      ticketNo: ticketId,
      ticketStatus: selectedStatus?.Description?.trim(),
    });
    toast.success(
      `Ticket status updated to ${selectedStatus.Description?.trim()?.toLowerCase()} successfully.`,
      {
        className: 'toast-success',
      },
    );
    queryClient.invalidateQueries(['ticketDetails', ticketId]);
  };

  if (isLoadingData || ticketResponseListingLoading) {
    return (
      <SpinnerLoading grow height="100vh" spinnerSize="2rem" color="#667085" />
    );
  }

  return (
    <div className="page-content ticket-details px-0">
      <div className="ticket-details-header-hr px-3 mt-2">
        <div className="d-flex flex-row align-items-center gap-3">
          <button
            onClick={() => {
              navigateBack(fr);
            }}
            type="button"
            className="btn btn-link p-0 d-flex flex-row align-items-center gap-1 back-btn"
          >
            <Icon path={mdiChevronLeft} size={1.0} />
            <span className="back fs-6">Back</span>
          </button>
          <div className="hr-ticket-details-separator" />
          <div className="d-flex flex-column align-items-center gap-0 align-items-md-start">
            <span className="fs-6">Ticket ID</span>
            <span className="fw-semibold text-dark fs-6">{ticketId}</span>
          </div>
          <div className="hr-ticket-details-separator" />
          <div className="d-flex flex-column align-items-center gap-0 align-items-md-start">
            <span className="fs-6">Created By</span>
            <span className="fw-semibold text-dark fs-6">
              {ticketDetails?.Requestor} | {ticketDetails?.RequestorCd}
            </span>
          </div>
          <div className="hr-ticket-details-separator" />
          <div className="d-flex flex-column align-items-center align-items-md-start">
            <span className="fs-6">Created On</span>
            <div className="ticket-created-badge">
              {DateUtil.isoToDdMmYyyySlice(ticketDetails?.CreatedOn)}
            </div>
          </div>
          {CLOSED_TICKET_STATUS.includes(ticketDetails?.Status) && (
            <>
              <div className="hr-ticket-details-separator" />
              <div className="d-flex flex-column align-items-center align-items-md-start">
                <span className="fs-6">Closed On</span>
                <div className="ticket-created-badge">
                  {DateUtil.isoToDdMmYyyySlice(ticketDetails?.StatusLastDate)}
                </div>
              </div>
            </>
          )}
        </div>
        <div>
          <div className="d-flex flex-row align-items-center gap-2">
            <div className="d-flex flex-row align-items-center gap-2">
              <span className="text-dark fw-medium fs-6">Status</span>
              <UncontrolledDropdown as="div">
                <DropdownToggle caret className="change-status-btn" as="span">
                  <span>
                    {selectedStatus?.Description || ticketDetails?.Status}
                  </span>
                </DropdownToggle>
                <DropdownMenu className="p-2">
                  {ticketStatusData?.map(option => (
                    <DropdownItem
                      className="status-option mb-2 py-2"
                      as="span"
                      key={option}
                      onClick={() => handleChangeStatus(option)}
                      active={ticketDetails?.Status === option?.Description}
                    >
                      {option?.Description}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
            {((roleName === ROLE_NAMES.HR &&
              userId === ticketDetails?.AssignedToId) ||
              roleName === ROLE_NAMES.ADMIN ||
              roleName === ROLE_NAMES.HR_ADMIN) && (
              <button
                onClick={() => {
                  setModelName('reassignTicketModal');
                  setIsModalOpen(true);
                }}
                type="button"
                className="reassign-ticket-btn d-flex flex-row align-items-center gap-1"
              >
                <img
                  src={reassignIcon}
                  alt="Re-Assign HR"
                  className="ticket-btn-icon"
                />
                <span>Re-Assign</span>
              </button>
            )}
            <button
              type="button"
              className="save-ticket-status-btn custom-btn"
              onClick={handleTicketStatusSave}
              disabled={
                ticketDetails?.Status === selectedStatus?.Description ||
                updateTicketMutation?.isPending ||
                !selectedStatus
              }
            >
              {updateTicketMutation?.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      <div className="d-flex flex-row align-items-start">
        <div className="w-50">
          <TicketDetailView />
        </div>
        <div className="w-50">
          <CommentSection />
        </div>
      </div>
      <DisplayModal
        isModalOpen={displayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
      <ModalMultiWrapper
        modals={{
          reassignTicketModal: {
            component: ReassignTicket,
            props: {
              handleOnSubmit: async (payload, reset) => {
                if (
                  payload?.reassignTo?.toLowerCase() ===
                  ticketDetails?.RequestorCd?.toLowerCase()
                ) {
                  toast.error('Reassigning to the requestor is not allowed.', {
                    className: 'toast-error',
                  });
                  return;
                }
                if (
                  payload?.reassignTo?.toLowerCase() ===
                  ticketDetails?.AssignedToCd?.toLowerCase()
                ) {
                  toast.error(
                    'Reassigning to the current assignee is not allowed.',
                    {
                      className: 'toast-error',
                    },
                  );
                  return;
                }
                try {
                  await updateTicketMutation.mutateAsync({
                    ticketNo: ticketId,
                    ...payload,
                  });
                  reset();
                  toast.success(
                    `Ticket #${ticketId || ''} reassigned successfully.`,
                    {
                      className: 'toast-success',
                    },
                  );
                  queryClient.invalidateQueries(['ticketDetails', ticketId]);
                  setIsModalOpen(false);
                } catch (error) {
                  if (error?.error) {
                    toast.error(error?.error, {
                      className: 'toast-error',
                    });
                  } else {
                    toast.error(
                      'Failed to reassign ticket. Please try again.',
                      {
                        className: 'toast-error',
                      },
                    );
                  }
                }
              },
              isLoading: updateTicketMutation?.isPending,
              ticketCategory: ticketDetails?.Category || '',
            },
          },
          cancelTicketModal: {
            component: CancelTicket,
            props: {
              handleOnSubmit: async payload => {
                if (roleType !== TICKET_ROLE_TYPES.CREATED_BY) {
                  toast.error('Only the creator can cancel the ticket.');
                  return;
                }
                try {
                  await cancelMyTicketMutation.mutateAsync({
                    ticketNo: ticketId,
                    ...payload,
                  });
                  toast.success('Ticket cancelled successfully.', {
                    className: 'toast-success',
                  });
                  navigateBack(fr);
                } catch (error) {
                  if (error?.error) {
                    toast.error(error?.error, {
                      className: 'toast-error',
                    });
                  } else {
                    toast.error('Failed to cancel ticket. Please try again.', {
                      className: 'toast-error',
                    });
                  }
                }
              },
              isLoading: cancelMyTicketMutation?.isPending,
            },
          },
        }}
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        selectedModal={modelName}
      />
    </div>
  );
}

export default HRTicketDetails;
